import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import AddIcon from "@mui/icons-material/Add";
import { deleteSectionData } from "../../../redux/slices/orgAdmin/promotionsSlice";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import {
  StyledButton,
  StyledContainer,
  StyledTableCell,
} from "../../CSS/Styles";
import {
  fetchSubscribers,
  saveSubscriber,
} from "../../../redux/slices/orgAdmin/subscribersSlice";
import { rowsPerPage } from "../../../Constants";

export default function Subscribers() {
  const dispatch = useDispatch();
  const { subscribersList, loading } = useSelector(
    (state) => state.subscribers
  );
  const [deleteTitle, setDeleteTitle] = useState("");
  const { companyId } = useSelector((state) => state.app);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionIdToDelete, setPromotionIdToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const sectionId = 6;

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const [isAddingSubscriber, setIsAddingSubscriber] = useState(false);
  const [currentSubscriber, setCurrentSubscriber] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const totalRecords =
    subscribersList?.pagination?.totalRecords || subscribersList?.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  useEffect(() => {
    dispatch(fetchSubscribers({ companyId, page, limit: rowsPerPage }));
  }, [dispatch, companyId, page]);

  const handleInputChange = (field, value) => {
    setCurrentSubscriber((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isEmailValid = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(
    currentSubscriber.email
  );

  const confirmDelete = () => {
    dispatch(deleteSectionData({ id: promotionIdToDelete }))
      .then(() => {
        setDeleteDialogOpen(false);
        setPromotionIdToDelete(null);
        // Fetch updated promotions list after successful deletion
        dispatch(fetchSubscribers({ companyId, page, limit: rowsPerPage }));
      })
      .catch((error) => {
        console.error("Error deleting promotion:", error);
      });
  };

  const handleDeleteClick = (id, title) => {
    setPromotionIdToDelete(id);
    setDeleteDialogOpen(true);
    setDeleteTitle(title);
  };

  const handleSuspendSubscriber = (subscriberItem) => {
    setCurrentSubscriber(subscriberItem);
    setIsAddingSubscriber(true);
  };

  const handleAddSubscriber = () => {
    setCurrentSubscriber({
      firstName: "",
      lastName: "",
      email: "",
    });
    setIsAddingSubscriber(true);
  };

  const handleFinalizeSubscriber = () => {
    // Prepare the payload with the subscriber's details
    const payload = {
      en: {
        firstName: currentSubscriber.firstName,
        lastName: currentSubscriber.lastName,
        email: currentSubscriber.email,
      },
    };

    // Dispatch the save action
    dispatch(saveSubscriber({ payload, sectionId }))
      .unwrap()
      .then(() => {
        // After successful save, fetch updated subscribers list
        dispatch(fetchSubscribers({ companyId, page, limit: rowsPerPage }));
      })
      .catch((error) => {
        console.error("Error saving subscriber:", error);
      });

    setConfirmationDialogOpen(false);
    setIsAddingSubscriber(false);
  };

  const isFormValid = () => {
    return (
      currentSubscriber.firstName.trim() &&
      currentSubscriber.lastName.trim() &&
      /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(currentSubscriber.email)
    );
  };

  return (
    <StyledContainer>
      <Paper elevation={3} sx={{ padding: 3 }}>
        {!isAddingSubscriber && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Subscribers
            </Typography>
            <StyledButton
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddSubscriber}
              sx={{ marginBottom: 2 }}
            >
              Add
            </StyledButton>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>First Name</StyledTableCell>
                    <StyledTableCell>Last Name</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>Subscription Date</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscribersList?.data?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.firstName}</TableCell>
                      <TableCell>{item.lastName}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.creation_date}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteClick(item.id, item.email)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
            <Dialog
              open={deleteDialogOpen}
              onClose={() => setDeleteDialogOpen(false)}
            >
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete{" "}
                  <span style={{ fontWeight: "bold" }}> {deleteTitle}</span>?
                  This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button color="error" onClick={confirmDelete}>
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {isAddingSubscriber && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <IconButton onClick={() => setIsAddingSubscriber(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Subscriber</Typography>
            </Box>

            <TextField
              sx={{ mt: 2 }}
              label="First Name"
              value={currentSubscriber.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Last Name"
              value={currentSubscriber.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Email"
              value={currentSubscriber.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              fullWidth
              error={!!currentSubscriber.email && !isEmailValid}
              helperText={
                !!currentSubscriber.email && !isEmailValid
                  ? "Please enter a valid email address"
                  : ""
              }
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setIsAddingSubscriber(false)}
                sx={{ marginRight: 2 }}
                color="error"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFinalizeSubscriber}
                color="primary"
                variant="contained"
                disabled={!isFormValid()}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </StyledContainer>
  );
}
